import {
  Box,
  Checkbox,
  IconButton,
  Link,
  Skeleton,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Edit,
  ExportSquare,
  More,
  Notification as NotificationIcon,
  TickCircle,
  Trash,
} from 'iconsax-react';
import { type FC, type ReactElement, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { hasStringValue, hasValue } from '@core/utils/equality';
import type { NotificationCardAction, NotificationCardItem } from '@core/utils/notificationCard';
import ClockIcon from '@web/assets/clock-icon.svg?react';

import { ClampedText } from '../ClampedText/ClampedText';
import { SlabCard } from '../SlabCard/SlabCard';
import { SlabMenu, SlabMenuAlign, type SlabMenuComponentProps } from '../SlabMenu/SlabMenu';
import { TypographyWithTooltip } from '../TypographyWithTooltip/TypographyWithTooltip';
import { recordLinkHref } from './utils';

const ACTION_CONFIG: Record<NotificationCardAction, { label: string; icon: ReactElement }> = {
  markAsRead: { label: 'Mark as Read', icon: <TickCircle size={20} /> },
  edit: { label: 'Edit', icon: <Edit size={20} /> },
  delete: { label: 'Delete', icon: <Trash size={20} /> },
};

const getColors = (
  theme: Theme,
  item: NotificationCardItem,
): { accentColor: string; iconBackgroundColor: string } => {
  if (item.kind === 'reminder') {
    return {
      accentColor: theme.palette.SlabYellow[800],
      iconBackgroundColor: theme.palette.SlabYellow[100],
    };
  }

  if (item.isUnread) {
    return {
      accentColor: theme.palette.SlabIndigo[600],
      iconBackgroundColor: theme.palette.SlabIndigo[100],
    };
  }
  return {
    accentColor: theme.palette.SlabGray[600],
    iconBackgroundColor: theme.palette.SlabGray[100],
  };
};

const MenuButton: FC<SlabMenuComponentProps> = (props) => (
  <IconButton size='medium' sx={{ flexShrink: 0 }} {...props}>
    <More variant='TwoTone' size={24} />
  </IconButton>
);

type Props = {
  item: NotificationCardItem;
  onClick?: () => void;
  onAction?: (action: NotificationCardAction) => void;
  isSelected?: boolean;
  shouldAnimateArrival?: boolean;
  onArrivalShown?: () => void;
  onSelectedChange?: (isSelected: boolean) => void;
};

export const NotificationCard: FC<Props> = ({
  item,
  onClick,
  onAction,
  isSelected = false,
  shouldAnimateArrival = false,
  onArrivalShown,
  onSelectedChange,
}) => {
  const isReminder = item.kind === 'reminder';
  const { record } = item;
  const href = recordLinkHref(record);

  const theme = useTheme();
  const { accentColor, iconBackgroundColor } = getColors(theme, item);

  // Latched on mount so the id can be forgotten right away: the card may unmount before the
  // animation ends, and a remount must not replay it.
  const [isArriving] = useState(shouldAnimateArrival);
  useEffect(() => {
    if (shouldAnimateArrival) {
      onArrivalShown?.();
    }
  }, [shouldAnimateArrival, onArrivalShown]);

  const isSelectable = hasValue(onSelectedChange);
  const handleClick = isSelectable ? (): void => onSelectedChange(!isSelected) : onClick;

  return (
    <SlabCard
      onClick={handleClick}
      sx={{
        marginX: 2,
        marginTop: 0,
        marginBottom: 1.5,
        padding: 2,
        flexShrink: 0,
        cursor: hasValue(handleClick) ? 'pointer' : 'default',
        ...(isSelected && { backgroundColor: 'SlabIndigo.50' }),
        ...(isArriving && {
          '@keyframes notificationArrival': {
            from: { backgroundColor: theme.palette.SlabIndigo[100] },
          },
          animation: 'notificationArrival 2s ease-out',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }),
        borderLeft: item.isUnread ? `3px solid ${accentColor}` : undefined,
        '&:hover': { backgroundColor: isSelected ? 'SlabIndigo.50' : 'SlabGray.50' },
      }}
    >
      <Box display='flex' alignItems='center' justifyContent='space-between' gap={1}>
        <Box display='flex' alignItems='center' gap={1} minWidth={0}>
          {isSelectable && (
            <Checkbox
              className='NotificationCard-checkbox'
              color='primary'
              size='small'
              checked={isSelected}
              onChange={(event): void => onSelectedChange(event.target.checked)}
              onClick={(event): void => event.stopPropagation()}
              inputProps={{ 'aria-label': `Select ${item.title}` }}
              sx={{
                flexShrink: 0,
                padding: 0.5,
                top: 1,
              }}
            />
          )}
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            flexShrink={0}
            width={40}
            height={40}
            borderRadius='50%'
            bgcolor={iconBackgroundColor}
          >
            {isReminder ? (
              <ClockIcon width={24} height={24} />
            ) : (
              <NotificationIcon size={24} variant='TwoTone' color={accentColor} />
            )}
          </Box>

          <TypographyWithTooltip text={item.title} variant='h6/medium'>
            {item.title}
          </TypographyWithTooltip>
          {hasStringValue(item.triggerLabel) && (
            <Box
              flexShrink={0}
              borderRadius={1}
              paddingX={1}
              paddingY={0.25}
              bgcolor='SlabYellow.100'
            >
              <Typography variant='body3' color='SlabYellow.800' whiteSpace='nowrap'>
                {item.triggerLabel}
              </Typography>
            </Box>
          )}
        </Box>
        {item.actions.length > 0 && (
          <Box onClick={(event): void => event.stopPropagation()} flexShrink={0}>
            <SlabMenu
              component={MenuButton}
              align={SlabMenuAlign.Right}
              options={item.actions.map((action) => ({
                label: ACTION_CONFIG[action].label,
                icon: ACTION_CONFIG[action].icon,
                onClick: (): void => onAction?.(action),
              }))}
            />
          </Box>
        )}
      </Box>

      <Box display='flex' flexDirection='column' gap={0.5} marginTop={1}>
        {item.details.map((detail) => (
          <Box key={detail.label} display='flex' alignItems='flex-start' gap={1}>
            <Typography variant='body3' color='SlabGray.600' flexShrink={0}>
              {`${detail.label}:`}
            </Typography>
            <Typography variant='body3' sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
              <ClampedText>{detail.value}</ClampedText>
            </Typography>
          </Box>
        ))}
      </Box>

      {hasValue(record) && hasStringValue(href) && (
        <Box display='flex' alignItems='center' justifyContent='flex-end' marginTop={1}>
          <Box
            display='flex'
            alignItems='center'
            onClick={(event): void => event.stopPropagation()}
          >
            <Link padding={0.5} component={RouterLink} to={href} variant='body3' underline='hover'>
              {record.label}
            </Link>
            <Link
              padding={0.5}
              href={href}
              target='_blank'
              rel='noopener'
              display='flex'
              aria-label={`Open ${record.label} in a new tab`}
            >
              <ExportSquare size={18} variant='TwoTone' color='currentColor' />
            </Link>
          </Box>
        </Box>
      )}
    </SlabCard>
  );
};

export const NotificationCardSkeleton: FC = () => (
  <SlabCard sx={{ marginX: 2, marginTop: 0, marginBottom: 1.5, padding: 2, flexShrink: 0 }}>
    <Box display='flex' alignItems='center' justifyContent='space-between' gap={1}>
      <Box display='flex' alignItems='center' gap={1} flex={1} minWidth={0}>
        <Skeleton variant='circular' width={40} height={40} sx={{ flexShrink: 0 }} />
        <Skeleton variant='text' width='55%' height={28} />
      </Box>
      <Skeleton variant='circular' width={24} height={24} sx={{ flexShrink: 0, marginX: 1 }} />
    </Box>

    <Box display='flex' flexDirection='column' gap={0.5} marginTop={1}>
      {[
        { label: 64, value: '45%' },
        { label: 88, value: '30%' },
        { label: 72, value: '38%' },
      ].map(({ label, value }) => (
        <Box key={label} display='flex' alignItems='center' justifyContent='space-between' gap={1}>
          <Skeleton variant='text' width={label} height={18} />
          <Skeleton variant='text' width={value} height={18} />
        </Box>
      ))}
    </Box>

    <Box display='flex' justifyContent='flex-end' marginTop={1}>
      <Skeleton variant='text' width={120} height={24} />
    </Box>
  </SlabCard>
);
